/* eslint-disable no-new-func */
/* eslint-disable no-param-reassign */
/* eslint-disable no-console */
import { MetricInfo, TimeSeriesAggregation, TimeSeriesResolution } from '@moderepo/mode-apis';
import { DataPoint, MultiMetricsDataPoint, MultiSeriesDataPoint, Time, TimeSeriesType, TimeSpan } from '@moderepo/mode-app-components';
import { useEffect, useState } from 'react';


/**
 * A work around for fetching collection data. Backend DOES NOT allow calling the GET collection data without at least 1 numeric metric
 * value name. If we have a metric definition that has no numericMetrics and only tagMetrics, we CAN NOT pass empty [] for valueNames.
 * So the work around is to pass 1 value name, ["DUMMY_VALUE_NAME"]. Because this value name does not exist, backend will just going to return null
 * for all its' values. Once we receive the data, we can just ignore the values returned for this DUMMY_VALUE_NAME.
 */
export const DUMMY_VALUE_NAME = 'DUMMY_VALUE_NAME';


// Maximum # of raw data points we can fetch
export const MAX_RAW_DATA_COUNT = 500;



export enum ChartSize {
    SMALL = 'small',
    MEDIUM = 'medium',
    LARGE = 'large',
}

/**
 * Define a new set of TimeSeriesAggregation to include AVG_MIN_MAX type. We can't extends TimeSeriesAggregation because that is
 * not allowed in Typescript
 */
export enum ExtTimeSeriesAggregation {
    MIN = 'min',
    MAX = 'max',
    AVG = 'avg',
    COUNT = 'count',
    SUM = 'sum',
    AVG_MIN_MAX = 'avg_min_max'
}


/**
 * When we setup metrics' data for AVG_MIN_MAX aggregation, we will combine the metrics' avg, min, and max data into 1 big data object.
 * The problem is we use metric's name as the key to the metric's data e.g.
 *      {
 *          temperature: [...],
 *          pressure: [...],
 *      }
 *
 * We can't use the same key for avg, min, and max data. Because AVG will be the primary data, will can continue to use the metric's name.
 * But for min and max data, they are more like secondary data therefore we will add some postfix to the key and these will be the strings
 * we will use. We will define these as constants and use these instead of hard coding '_min', and '_max' everywhere.
 * The data object will look like this
 *      {
 *          temperature: [...],
 *          temperature_min: [...],
 *          temperature_max: [...],
 *          pressure: [...],
 *          pressure_min: [...],
 *          pressure_max: [...],
 *      }
 */
export const MIN_DATA_FIELD_POSTFIX = '_min';
export const MAX_DATA_FIELD_POSTFIX = '_max';



/**
 * User friendly name for Time Span types which we can display to the users
 */
export const getTimeSpanName = (type: TimeSpan): string => {
    const names = Object.freeze({
        [TimeSpan.MINUTE]: 'time_span.one_minute',
        [TimeSpan.HOUR]  : 'time_span.one_hour',
        [TimeSpan.DAY]   : 'time_span.one_day',
        [TimeSpan.WEEK]  : 'time_span.one_week',
        [TimeSpan.MONTH] : 'time_span.one_month',
        [TimeSpan.YEAR]  : 'time_span.one_year',
    });

    return names[type];
};



/**
 * User friendly name for Time Series types which we can display to the users
 */
export const getTimeSeriesTypeName = (type: TimeSeriesType): string => {
    const names = Object.freeze({
        [TimeSeriesType.SIMPLE]    : 'time_series_type.simple',
        [TimeSeriesType.COLLECTION]: 'time_series_type.collection',
    });

    return names[type];
};



/**
 * User friendly name for EXTENDED Time Series aggregation types which we can display to the users
 */
export const getExtTimeSeriesAggregationTypeName = (type: ExtTimeSeriesAggregation): string => {
    const names = Object.freeze({
        [ExtTimeSeriesAggregation.AVG]        : 'time_series.aggregation_type.avg',
        [ExtTimeSeriesAggregation.COUNT]      : 'time_series.aggregation_type.count',
        [ExtTimeSeriesAggregation.MAX]        : 'time_series.aggregation_type.max',
        [ExtTimeSeriesAggregation.MIN]        : 'time_series.aggregation_type.min',
        [ExtTimeSeriesAggregation.SUM]        : 'time_series.aggregation_type.sum',
        [ExtTimeSeriesAggregation.AVG_MIN_MAX]: 'time_series.aggregation_type.avg_min_max',
    });

    return names[type];
};


/**
 * User friendly name for Time Series aggregation types which we can display to the users
 */
export const getTimeSeriesAggregationTypeName = (type: TimeSeriesAggregation): string => {
    return getExtTimeSeriesAggregationTypeName((type as unknown) as ExtTimeSeriesAggregation);
};



/**
 * Localize the MultiSeriesDataPoint array
 */
export const useConvertMultiSeriesToMultiMetricsDataPoints = (
    dataPoints: readonly MultiSeriesDataPoint<number | string>[] | undefined,
): readonly MultiMetricsDataPoint[] | undefined => {
    const [convertedDataPoints, setConvertedDataPoints] = useState<MultiMetricsDataPoint[]>();

    useEffect(() => {
        if (dataPoints) {
            setConvertedDataPoints(dataPoints.map((dataPoint): MultiMetricsDataPoint => {
                return {
                    dataPointTimestamp: dataPoint.dateValue,
                    ...dataPoint.values,
                };
            }));
        }
    }, [dataPoints]);

    return convertedDataPoints;
};



/**
 * Merge 2 arrays of MultiSeriesDataPoint (1 for numeric metrics and 1 for tag metrics) into 1 array
 * @param array1
 * @param array2
 */
export const useMergeMetricMultiSeriesDataPoints = <T extends string | number>(
    array1: readonly MultiSeriesDataPoint<T>[] | undefined,
    array2: readonly MultiSeriesDataPoint<T>[] | undefined,
): readonly MultiSeriesDataPoint<T>[] | undefined => {
    const [mergedArray, setMergedArray] = useState<readonly MultiSeriesDataPoint<T>[] | undefined>();

    useEffect(() => {
        if (array1 || array2) {
            if (array1 && array2 && array1.length === array2.length) {
                setMergedArray(array1?.map((item: MultiSeriesDataPoint<T>, index: number) => {
                    return {
                        ...item,
                        values: {
                            ...item.values,
                            ...array2[index].values,
                        },
                    };
                }));
            } else {
                setMergedArray(array1 || array2);
            }
        }
    }, [array1, array2]);

    return mergedArray;
};



/**
 * Sort the list of metrics by the order they were defined
 * @param metrics - The target metrics array we want to sort
 * @param metricDefinition - The metrics definition array
 * @returns
 */
export const sortMetricsByMetricsDefinition = <T extends MetricInfo>(
    metrics: readonly T[], metricDefinition: readonly T[] | undefined,
): readonly T[] => {

    if (metricDefinition) {
        /**
         * Create a map of the metric's name => metric's index in metric definition. Lets say the metrics are defined in this order
         * ["pressure", "humidity", "temperature"] therefore this will produce something like this
         *      metricsIndices = {
         *          temperature: 2,
         *          pressure: 0,
         *          humidity: 1
         *      }
         */
        const metricsIndices = metricDefinition.reduce((result, metric, index): {
            [valueName: string]: number
        } => {
            return {
                ...result,
                [metric.name]: index,
            };
        }, {});

        // Now sort the metrics by comparing their indices
        return [...metrics].sort((metric1: T, metric2: T) => {
            const index1 = metricsIndices[metric1.name] || 0;
            const index2 = metricsIndices[metric2.name] || 0;
            return index1 - index2;
        });
    }

    // If there is no metricDefinition, return the original metrics
    return metrics;
};



/**
 * User friendly name for EXTENDED Time Series aggregation types which we can display to the users
 */
export const getTimeSeriesResolutionName = (type: TimeSeriesResolution): string => {
    const names = Object.freeze({
        [TimeSeriesResolution.SEC_5]  : 'time_series.resolution.5sec',
        [TimeSeriesResolution.SEC_15] : 'time_series.resolution.15sec',
        [TimeSeriesResolution.MIN_1]  : 'time_series.resolution.1min',
        [TimeSeriesResolution.MIN_10] : 'time_series.resolution.10min',
        [TimeSeriesResolution.HOUR_1] : 'time_series.resolution.1hour',
        [TimeSeriesResolution.DAY_1]  : 'time_series.resolution.1day',
        [TimeSeriesResolution.WEEK_1] : 'time_series.resolution.1week',
        [TimeSeriesResolution.MONTH_1]: 'time_series.resolution.1month',
    });

    return names[type];
};


/**
 * Given a TimeSeriesResolution, return the value it represents in milliseconds.
 */
export const getTimeSeriesResolutionValueInMS = (type: TimeSeriesResolution): number => {
    switch (type) {
        case TimeSeriesResolution.SEC_5:
            return Time.SECOND_IN_MS * 5;
        case TimeSeriesResolution.SEC_15:
            return Time.SECOND_IN_MS * 15;
        case TimeSeriesResolution.MIN_1:
            return Time.MINUTE_IN_MS;
        case TimeSeriesResolution.MIN_10:
            return Time.MINUTE_IN_MS * 10;
        case TimeSeriesResolution.HOUR_1:
            return Time.HOUR_IN_MS;
        case TimeSeriesResolution.DAY_1:
            return Time.DAY_IN_MS;
        case TimeSeriesResolution.WEEK_1:
            return Time.WEEK_IN_MS;
        case TimeSeriesResolution.MONTH_1:
            return Time.MONTH_IN_MS;
        default:
            return 1;
    }
};


/**
 * This is the data structure for caching time series data. We will store the time series data we loaded for each metric
 * in this cache. Whenever the user change the filter e.g. select/deselect metric, change start/end date, we will first
 * check this cache to see if we already have data for the current filters. If yes, we will use the data from cache. If not,
 * we will fetch time series data and cache them.
 * Because we have 2 types of metric data, numeric and tag, and they can have the name, we will separate the cache into 2 separate
 * objects, numericMetrics and tagMetrics.
 *
 * start - The start time of the cached data series.
 * end - The end time of the cached data series.
 * limit - The number of data points in the data. This is only used with RAW time series data
 * dataResolution - the resolution of the data points e.g. 10min (1 point every 10 minutes)
 * dataResolutionValue - the resolution value in milliseconds e.g 10min => 600000
 * aggregation - The aggregation function used when fetching data
 * numericMetrics - A map of NUMERIC metric name => array of data points
 * tagMetrics - A map of TAG metric name => array of data points
 */
export interface CachedMetricData {
    readonly start: number | undefined;
    readonly end: number | undefined;
    readonly limit?: number | undefined;
    readonly dataResolution?: TimeSeriesResolution | undefined;
    readonly dataResolutionValue?: number | undefined;
    readonly aggregation?: ExtTimeSeriesAggregation | TimeSeriesAggregation | undefined;
    readonly numericMetrics?: {
        readonly [metricValueName: string]: readonly DataPoint<number>[];
    };
    readonly tagMetrics?: {
        readonly [metricName: string]: readonly DataPoint<string>[];
    };
}



/**
 * Transform a given data (multiMetricData) values based on the provided formula.
 *
 * @param multiMetricData - The Array of MultiMetric data. The data format is like this. It is an Array of objects and each object contains
 *                  values for all metrics and a date.
 *                  multiMetricData = [{
 *                      date: "Jan 1", temperature: 45, pressure: 67, battery: 34
 *                  }, {
 *                      date: "Jan 2", temperature: 43, pressure: 23, battery: 45
 *                  }, {
 *                      date: "Jan 3", temperature: 65, pressure: 46, battery: 36
 *                  }]
 *
 * @param transformFormulaByMetricName - The formula that will be used to calculate the new value for EACH metric. Each collection contains data for
 *                  multiple metrics and each metric can have a transformation formula. Not all metrics need a transformation formula. Any metric
 *                  that don't have transformation formula will be left as is. This is a map of metricNamed => formula instead of just 1 formula
 *                  because it is more efficient to loop through the collection data row by row and apply the transformation for all metrics
 *                  instead of doing it one metric at a time.
 *
 * For example: given this collection data with 3 metrics, temperature (in Celsius), pressure, battery (battery level remain in %)
 *              temperature    pressure    battery
 *      Jan 1       23            45         78
 *      Jan 2       18            23         73
 *      Jan 3       29            64         45
 *      Jan 4       19            54         32
 *      Jan 5       23            29         12
 *
 * And we want to convert temperature value from celsius and battery value from % left to % used. Which mean the "transformFormulaByMetricName"
 * will be
 *      transformFormulaByMetricName = {
 *          temperature: "Math.floor(({{temperature}} * 9/5) + 32)",
 *          battery: "100 - {{battery}}"
 *      }
 *
 * This will be the output
 *              temperature    pressure    battery
 *      Jan 1       73            45         22
 *      Jan 2       64            23         27
 *      Jan 3       84            64         55
 *      Jan 4       66            54         88
 *      Jan 5       73            29         88
 */
export const transformMultiMetricsData = (
    multiMetricData: MultiMetricsDataPoint[],
    transformFormulaByMetricName: {
        readonly [metricName: string]: string | undefined;
    },
): MultiMetricsDataPoint[] => {

    if (Object.keys(transformFormulaByMetricName).length === 0) {
        // If the "transformFormulaByMetricName" object is empty which mean none of the metrics needs transformation
        // therefore we can return the same data.
        return multiMetricData;
    }

    // Create a Function for each transform formula. We will call these functions later.
    const transformFunctionPerMetric: {
        [metricName: string]: Function;
    } = {};
    Object.entries(transformFormulaByMetricName).forEach(([metricName, formula]) => {
        if (formula) {

            // Get all the place holders from the formula e.g. {{temperature}}, {{pressure}}, etc...
            const placeholders: string[] = [];

            // eslint-disable-next-line no-restricted-syntax
            for (const match of formula.matchAll(/{{(.*?)}}/g)) {
                const placeholder = match[0];
                if (!placeholders.includes(placeholder)) {
                    placeholders.push(placeholder);
                }
            }

            /**
             * Modified the formula to something we can execute.
             *
             * The formula will be in this format, something like this
             *      formula = "{{temperature}} + {{pressure}} * 5"
             *
             * Our data is an object like this
             *      data = {
             *          temperature: 45, pressure: 10
             *      }
             *
             * When we create a function to execute the formula, we have 2 ways.
             * 1: Pass each value in the data to the function as individual param
             *      function (temperature, pressure) {
             *          return temperature + pressure * 5
             *      }
             *    But this mean we will need to deconstruct the data object to separate the "temperature" and "pressure" as different params.
             *    Another problem with this approach is that the metric name "temperature" must be value variable names. If a metric name is
             *    like this "temperature-01" then this won't work.
             *
             * 2. Pass the entire data object to the function
             *      function (data) {
             *          return data["temperature"] + data["pressure"] * 5
             *      }
             *    This was we don't need to deconstruct the data object but we need to change the placeholders from "{{name}}" to "data["name"]"
             *
             * We will go with #2. We will replace all placeholders in this format {{name}} to this data["name"]
             */
            const DATA_PARAM_NAME = 'data';
            const modifiedFormula = placeholders.reduce((modifiedFormula, placeholder) => {
                const placeholderValue = placeholder.replace(/[{}]/g, '');
                return modifiedFormula.replaceAll(placeholder, `${DATA_PARAM_NAME}["${placeholderValue}"]`);
            }, formula);


            try {
                // Create a function that take a "data" object and run the formula to calculate and return a new value
                transformFunctionPerMetric[metricName] = new Function(DATA_PARAM_NAME, `return (${modifiedFormula})`);
            } catch (error) {
                // unable to create a function for the given formula. The formula must be invalid. For this case, we will create a function
                // that does nothing and return null as value. We will have it return null instead of 0 because 0 is a good metric value.
                transformFunctionPerMetric[metricName] = new Function(DATA_PARAM_NAME, 'return (null)');

                // Log a warning that the formula is invalid because it tries to use a metrics definition that does not exist
                console.log(`%cInvalid formula: '${(error as Error).message}'`, 'color:red; font-weight:bold;', { formula, modifiedFormula });
            }
        }
    });


    // Now go through each element in the multiMetricData and transform the values in each row based on the formulas
    multiMetricData.forEach((oneDataRow) => {

        // For each metric that has transform formula function created, call the function to calculate a new value for the metric and then
        // replace the metric's current value with the new value.
        Object.entries(transformFunctionPerMetric).forEach(([metricName, transformFunction]) => {
            if (transformFunction) {
                try {
                    // Call the transform function and pass it to object that contain the value for all metrics so that the function can
                    // use the data
                    const newValue = transformFunction(oneDataRow);
                    if (typeof newValue === 'number' && !Number.isNaN(newValue)) {
                        oneDataRow[metricName] = newValue;
                    }
                } catch (error) {
                    oneDataRow[metricName] = null;
                }
            }
        });
    });

    return multiMetricData;
};

/**
 * Given an Array of data points, find the resolution of the data based on the data points' timestamps.
 *
 */
export const getDataResolution = (dataPoints: ReadonlyArray<MultiMetricsDataPoint>): {
    readonly timeUnit: 'second' | 'minute' | 'hour' | 'day' | 'week' | 'month';
    readonly amount: number;
} => {
    if (dataPoints) {
        // Loop through the data points to find the shortest time interval between any 2 data points.
        let shortestInterval = Number.MAX_SAFE_INTEGER;
        for (let i = 0; i < dataPoints.length - 1; i += 1) {
            if (dataPoints[i] !== undefined && dataPoints[i + 1] !== undefined) {
                shortestInterval = Math.min(shortestInterval, dataPoints[i + 1].dataPointTimestamp - dataPoints[i].dataPointTimestamp);
            }
        }

        if (shortestInterval >= Time.MONTH_IN_MS) {
            return {
                timeUnit: 'month',
                amount  : Math.floor(shortestInterval / Time.MONTH_IN_MS),
            };
        }
        if (shortestInterval >= Time.WEEK_IN_MS) {
            return {
                timeUnit: 'week',
                amount  : Math.floor(shortestInterval / Time.WEEK_IN_MS),
            };
        }
        if (shortestInterval >= Time.DAY_IN_MS) {
            return {
                timeUnit: 'day',
                amount  : Math.floor(shortestInterval / Time.DAY_IN_MS),
            };
        }
        if (shortestInterval >= Time.HOUR_IN_MS) {
            return {
                timeUnit: 'hour',
                amount  : Math.floor(shortestInterval / Time.HOUR_IN_MS),
            };
        }
        if (shortestInterval >= Time.MINUTE_IN_MS) {
            return {
                timeUnit: 'minute',
                amount  : Math.floor(shortestInterval / Time.MINUTE_IN_MS),
            };
        }
        if (shortestInterval >= Time.SECOND_IN_MS) {
            return {
                timeUnit: 'second',
                amount  : Math.floor(shortestInterval / Time.SECOND_IN_MS),
            };
        }
    }

    // Return 1 second by default
    return {
        timeUnit: 'second',
        amount  : 1,
    };
};
